import React, { useState } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Lended monogram as inline SVG → data-URI (works in Gmail / Outlook / Apple Mail signature editors)
const LOGO_SVG = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect x='14' y='8' width='10' height='3' fill='%23FF6B1A'/><path d='M14 12 H24 V44 H46 V54 H14 Z' fill='%230A0A0A'/><rect x='50' y='44' width='6' height='10' fill='%23FF6B1A'/></svg>`;
const LOGO_DATA_URI = `data:image/svg+xml;utf8,${LOGO_SVG}`;

const PEOPLE = [
    { first: "Ralph", last: "Missry", title: "Owner & Head Funder", phoneRaw: "5167305656", phoneDisplay: "(516) 730-5656", email: "Ralph@lended.com" },
    { first: "Jordan", last: "", title: "Submissions", phoneRaw: "5167305656", phoneDisplay: "(516) 730-5656", email: "jordan@lended.com" },
    { first: "Jeffrey", last: "", title: "Senior Funding Specialist", phoneRaw: "5167305656", phoneDisplay: "(516) 730-5656", email: "jeffrey@lended.com" },
];

// Build table-based HTML signature (max compatibility with Gmail / Outlook / Apple Mail)
function buildSignatureHTML(p) {
    const fullName = p.last ? `${p.first} ${p.last}` : p.first;
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#0A0A0A;font-size:14px;line-height:1.5;">
  <tr>
    <td style="padding:0 24px 0 0;border-right:2px solid #FF6B1A;vertical-align:top;">
      <img src="${LOGO_DATA_URI}" alt="Lended" width="68" height="68" style="display:block;border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;" />
      <div style="font-family:Arial,Helvetica,sans-serif;font-weight:900;font-size:22px;letter-spacing:-1.2px;color:#0A0A0A;margin-top:8px;line-height:1;">lended<span style="color:#FF6B1A;">.</span></div>
      <div style="font-family:Arial,Helvetica,sans-serif;font-weight:700;font-size:9px;letter-spacing:1.5px;color:#0A0A0A;margin-top:10px;">FAST. RELIABLE. RESULTS.</div>
    </td>
    <td style="padding:0 0 0 24px;vertical-align:top;">
      <div style="font-family:Arial,Helvetica,sans-serif;font-weight:800;font-size:22px;color:#0A0A0A;line-height:1.1;">${fullName}</div>
      <div style="font-family:Arial,Helvetica,sans-serif;font-weight:500;font-size:13px;color:#525252;margin-top:4px;">${p.title} &nbsp;|&nbsp; <span style="color:#FF6B1A;font-weight:700;">Lended</span></div>
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:14px;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#0A0A0A;">
        <tr>
          <td style="padding:3px 8px 3px 0;color:#FF6B1A;font-weight:700;width:18px;">P</td>
          <td style="padding:3px 0;"><a href="tel:${p.phoneRaw}" style="color:#0A0A0A;text-decoration:none;">${p.phoneDisplay}</a></td>
        </tr>
        <tr>
          <td style="padding:3px 8px 3px 0;color:#FF6B1A;font-weight:700;">E</td>
          <td style="padding:3px 0;"><a href="mailto:${p.email}" style="color:#0A0A0A;text-decoration:none;">${p.email}</a></td>
        </tr>
        <tr>
          <td style="padding:3px 8px 3px 0;color:#FF6B1A;font-weight:700;">W</td>
          <td style="padding:3px 0;"><a href="https://lended.com" style="color:#0A0A0A;text-decoration:none;">lended.com</a></td>
        </tr>
      </table>
      <div style="margin-top:14px;">
        <a href="https://lended.com/#apply" style="display:inline-block;background:#FF6B1A;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-weight:800;font-size:12px;letter-spacing:1px;text-transform:uppercase;text-decoration:none;padding:10px 20px;border:2px solid #FF6B1A;">&raquo;&nbsp; APPLY NOW</a>
      </div>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding-top:18px;">
      <div style="font-family:Arial,Helvetica,sans-serif;font-size:9.5px;color:#888888;line-height:1.5;max-width:640px;">
        This e-mail and any attachments may contain confidential and privileged information intended solely for the attention and use of the named addressee(s). If you are not the intended recipient, you are hereby notified that any unauthorized review, use, disclosure, copying, or distribution of this e-mail or its attachments is strictly prohibited. If you have received this e-mail in error, please notify the sender immediately and delete it from your system. Any views or opinions presented in this e-mail are solely those of the author and do not necessarily represent those of Lended.
      </div>
    </td>
  </tr>
</table>`;
}

function SignatureCard({ person }) {
    const html = buildSignatureHTML(person);
    const [copied, setCopied] = useState(false);

    const copyHTML = async () => {
        try {
            await navigator.clipboard.writeText(html);
            setCopied(true);
            toast.success("HTML copied — paste into Gmail signature settings");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Copy failed — select & copy from the textarea below");
        }
    };

    const copyRendered = async () => {
        try {
            const blob = new Blob([html], { type: "text/html" });
            const data = [new ClipboardItem({ "text/html": blob, "text/plain": new Blob([html], { type: "text/plain" }) })];
            await navigator.clipboard.write(data);
            toast.success("Rendered signature copied — paste straight into Gmail");
        } catch {
            copyHTML();
        }
    };

    return (
        <div className="bg-white border-2 border-[#0A0A0A] mb-10" data-testid={`signature-${person.first.toLowerCase()}`}>
            <div className="flex items-center justify-between bg-[#0A0A0A] text-white px-6 py-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">Signature</div>
                    <div className="font-display font-bold text-lg">{person.first}{person.last ? ` ${person.last}` : ""}</div>
                </div>
                <div className="flex gap-2">
                    <button onClick={copyRendered} className="bg-[#FF6B1A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-[#E65A12] inline-flex items-center gap-2" data-testid={`copy-rendered-${person.first.toLowerCase()}`}>
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        Copy Signature
                    </button>
                    <button onClick={copyHTML} className="bg-white text-black text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-white/90 inline-flex items-center gap-2" data-testid={`copy-html-${person.first.toLowerCase()}`}>
                        <Copy size={14} /> Copy HTML
                    </button>
                </div>
            </div>

            <div className="p-8 bg-white overflow-x-auto">
                <div dangerouslySetInnerHTML={{ __html: html }} />
            </div>

            <details className="border-t border-black/10">
                <summary className="cursor-pointer px-6 py-3 text-xs font-mono uppercase tracking-wider text-black/55 hover:text-black">View raw HTML</summary>
                <textarea
                    readOnly
                    value={html}
                    className="w-full h-64 p-4 font-mono text-xs bg-[#F8F5F2] border-t border-black/10 outline-none"
                    onFocus={(e) => e.target.select()}
                />
            </details>
        </div>
    );
}

export default function Signatures() {
    usePageTitle("Email Signatures");
    return (
        <section className="bg-[#F8F5F2] py-16 md:py-24 min-h-screen" data-testid="signatures-page">
            <div className="container-lended max-w-4xl">
                <span className="eyebrow">Internal Tool</span>
                <h1 className="font-display text-5xl md:text-6xl font-extrabold tracking-[-0.04em] mt-3 leading-[0.95]">
                    Email Signatures
                </h1>
                <p className="mt-5 text-lg text-black/70 max-w-2xl">
                    Three signatures, ready to paste into Gmail. Click <strong>Copy Signature</strong>, then in Gmail go to{" "}
                    <span className="font-mono bg-white px-2 py-0.5 border border-black/15">Settings → See all settings → General → Signature → Create new</span>{" "}
                    and paste. Save changes. Done.
                </p>

                <div className="mt-12">
                    {PEOPLE.map((p) => <SignatureCard key={p.first} person={p} />)}
                </div>

                <div className="mt-12 bg-white border-2 border-[#0A0A0A] p-6 text-sm text-black/70">
                    <div className="font-display font-bold text-base text-black mb-2">Email addresses</div>
                    <p>
                        Owner: <span className="font-mono">Ralph@lended.com</span>. Submissions: <span className="font-mono">submissions@lended.com</span> (CC <span className="font-mono">jordan@lended.com</span>). Team: <span className="font-mono">first@lended.com</span>.
                    </p>
                </div>
            </div>
        </section>
    );
}
